// Admin: Headhunter / Staffing (DEV-351) domain types.
// Extracted from lib/types.ts so the staffing admin surface is a self-contained
// module. Re-exported from @/lib/types, so `@/lib/types` imports still resolve.

import type { QuestStatus } from "./types";
import type { CandidateVolunteerHistory } from "./volunteerHistory";

// ─── Admin: Headhunter / Staffing (DEV-351) ───────────────
// The admin-only view of the auto-staffing pipeline. Unlike the creator view
// (which hides sourced contact data), admins audit EVERYTHING the worker does.

export const CANDIDATE_SOURCES = ["internal", "external"] as const;
export type CandidateSource = (typeof CANDIDATE_SOURCES)[number];

// Mirrors the `status` CHECK on quest_candidates (PR #146 migration).
export const ADMIN_CANDIDATE_STATUSES = [
  "suggested",
  "reviewed",
  "invited",
  "applied",
  "dismissed",
] as const;
export type AdminCandidateStatus = (typeof ADMIN_CANDIDATE_STATUSES)[number];

// The funnel transitions an admin may set by hand. 'applied' is excluded — it
// means the person signed up via an outreach link, which only the conversion
// flow (#147) may assert, never a manual click. Mirrors admin_set_candidate_status.
export const ADMIN_SETTABLE_CANDIDATE_STATUSES = [
  "suggested",
  "reviewed",
  "invited",
  "dismissed",
] as const;
export type AdminSettableCandidateStatus =
  (typeof ADMIN_SETTABLE_CANDIDATE_STATUSES)[number];

// Mirrors the `entity_type` CHECK on quest_candidates (DEV-474). "business" rows
// are Google Maps listings: admin-manual contact only, never auto-emailed.
export const CANDIDATE_ENTITY_TYPES = ["person", "business"] as const;
export type CandidateEntityType = (typeof CANDIDATE_ENTITY_TYPES)[number];

/**
 * What a manual run is looking for — the admin's explicit answer, mirroring the
 * testbed's `Buscamos` control.
 *
 * DEV-579. Absent means "let the model decide" and is deliberately different
 * from an asserted `"either"`.
 */
export type SearchTargetType = "person" | "business" | "either";

/**
 * The `error` code the admin-run-headhunter edge function returns with 409 when
 * the quest already has a run in flight.
 *
 * DEV-579. A code rather than prose so the UI owns the wording and its
 * translations; the edge function has no locale. Single-sourced here because
 * both the error mapping in the run hook and the manual-run card's inline
 * message compare against it — two string literals would drift the day the
 * code changes on one side only.
 */
export const RUN_IN_PROGRESS_CODE = "IN_PROGRESS";

// Mirrors the `linkedin_outreach_status` CHECK on quest_candidates (DEV-351e).
export const LINKEDIN_OUTREACH_STATUSES = ["none", "sent", "opted_out"] as const;
export type LinkedinOutreachStatus = (typeof LINKEDIN_OUTREACH_STATUSES)[number];

// Mirrors the `dismissal_reason` CHECK on quest_candidates (DEV-377).
export const DISMISSAL_REASONS = [
  "skills_mismatch",
  "location",
  "seniority",
  "availability",
  "already_known",
  "other",
] as const;
export type DismissalReason = (typeof DISMISSAL_REASONS)[number];

/** Structured "why is this a good candidate" verdict (DEV-439). */
export interface CandidateAiAssessment {
  verdict: string;
  pros: string[];
  cons: string[];
}

/** A candidate row as the admin sees it — includes sourced contact + provenance. */
export interface AdminQuestCandidate {
  id: string;
  questId: string;
  questTitle: string | null;
  source: CandidateSource;
  memberId: string | null;
  convertedUserId: string | null;
  fullName: string;
  locationCity: string | null;
  locationCountry: string | null;
  skills: string[];
  employmentTitle: string | null;
  linkedinUrl: string | null;
  email: string | null;
  phone: string | null;
  emailVerified: boolean | null;
  score: number | null;
  status: AdminCandidateStatus;
  // When weXare contacted this person (human invite now; automated send later, #147).
  // null = not yet contacted = "needs outreach". The cooldown sweep keys off this.
  outreachSentAt: string | null;
  // Provenance / compliance (PII guardrails migration).
  sourceUrl: string | null;
  collectedAt: string | null;
  lawfulBasis: string | null;
  purpose: string | null;
  lastRefreshedAt: string | null;
  createdAt: string | null;
  // LinkedIn draft outreach (DEV-351e) — human-send state.
  linkedinOutreachStatus: LinkedinOutreachStatus;
  linkedinSentAt: string | null;
  // Dismissal audit trail (DEV-377). Both null when status !== 'dismissed'.
  dismissalReason: DismissalReason | null;
  dismissalNote: string | null;
  // Admin-added source URLs (LinkedIn, portfolios, articles) that feed the AI profile.
  sourceUrls: string[];
  // Claude-generated candidate summary; null until first generation.
  aiProfile: string | null;
  aiProfileGeneratedAt: string | null;
  // Structured fit assessment (DEV-439); null until first generation.
  aiAssessment: CandidateAiAssessment | null;
  aiAssessmentGeneratedAt: string | null;
  // Resume URL (DEV-384). Admin-pasted link; null when not set.
  resumeUrl: string | null;
  // AI enrichment (DEV-386). Field names the AI filled; empty = not AI-enriched.
  // Editing a field removes it from this array (admin_clear_candidate_ai_field RPC).
  aiEnrichedFields: string[];
  // Person-level identity across quests (DEV-450). Null for unidentifiable
  // candidates (no linkedin/email/name+city match).
  candidateIdentityId: string | null;
  // How many OTHER quests this same identity currently has candidate rows in.
  // 0 when candidateIdentityId is null or the identity appears in no other quest.
  otherQuestCount: number;
  // DEV-473: the single platform this candidate was sourced from (e.g.
  // "facebook", "github"), or null for FullEnrich/internal-sourced candidates.
  sourcePlatform: string | null;
  // DEV-481: per-platform public profile URLs captured by search
  // (e.g. {"tiktok": "https://tiktok.com/@x"}), or null.
  socialProfiles: Record<string, string> | null;
  // DEV-474: what KIND of thing this candidate is. "business" rows come from
  // Google Maps and are ADMIN-MANUAL contact only — they are excluded from the
  // automated outreach path, so the UI must make the distinction visible or an
  // admin cannot know to reach them by phone instead.
  entityType: CandidateEntityType;
  // DEV-474: Google Places id for business rows; null for people.
  mapsPlaceId: string | null;
  // DEV-499b: Google Maps formatted street address for business rows; null for
  // people (and for address-less listings).
  formattedAddress: string | null;
  // DEV-499b: clickable Google Maps URI for business rows; null when no URI
  // could be built (no vendor URI and no place id to look up).
  mapsUri: string | null;
  // DEV-503: true when `skills` is an ECHO of the search query that found this
  // candidate (the social and web legs supply no skill data, so the worker wrote
  // our own terms into the field) rather than anything the source attested.
  //
  // The UI MUST NOT render these as the person's skills. They are search context
  // — "we looked for this and they came back" — and presenting them as attributes
  // is what makes an admin unable to tell what we actually know about a stranger
  // before deciding to contact them.
  skillsFromQuery: boolean;
  // DEV-503: true when locationCity/locationCountry were inherited from the
  // search query's fallback because the hit carried none of its own. Render as
  // inferred-from-search, not as the person's location. Tracked by the worker
  // since DEV-491 but only persisted (and therefore only readable here) as of
  // DEV-503 — rows sourced before that migration report false.
  locationFromQuery: boolean;
  // DEV-807: the member's OWN connected email channel (user_contacts, provider
  // 'email', not revoked), resolved live by admin_list_quest_candidates. Null for
  // every external row and for any member with no active email channel.
  //
  // A FIELD OF ITS OWN, never merged into `email`. That one is SOURCED contact —
  // data we collected about a stranger, and the thing R4 retention purges, R6
  // suppression hashes and identity.ts resolves identities on. This one is a
  // member's own address held under consent, used by the internal outreach path
  // (outreach/wiring.ts selectInternalBatch) which resolves it the same way at
  // send time. Rendering them from one field would make an admin unable to tell
  // which of the two a card is showing, and would invite exactly the "just write
  // it into quest_candidates.email" shortcut that the separation exists to
  // prevent.
  memberEmail: string | null;
  // DEV-822: the lead that holds this person, resolved live by
  // admin_list_quest_candidates. Null when no lead holds them — which is what the
  // absent badge means, and what makes "Create lead" an offer worth making.
  //
  // "HOLDS THIS PERSON" IS NOT "WAS CREATED FROM THIS CANDIDATE". The RPC matches
  // on source_candidate_id, else normalized email, else normalized phone, because
  // that is how admin_create_lead_from_candidate resolves its OWN conflicts: it
  // is idempotent on the CONTACT, and when its insert conflicts it returns the
  // existing lead without stamping source_candidate_id on it. So a lead first
  // created by hand or by CSV import still shows here. Keying this off provenance
  // alone is what DEV-822 exists to fix.
  //
  // An id and a timestamp, never the lead's own contact facts — an admin may have
  // corrected an unclaimed lead's email, and a second diverging copy of a contact
  // on a row that already carries its own would be worse than nothing.
  leadId: string | null;
  // DEV-822: when the person claimed that lead, or null while it is still an
  // unclaimed prefill. Read through candidateLeadState, not directly.
  leadClaimedAt: string | null;
  // DEV-TBD: true when an admin entered this candidate by hand. The worker's
  // draft sweep excludes manuallyAdded = true, so these candidates are
  // admin-review-only and never enter automated outreach.
  manuallyAdded: boolean;
  // 20260923100000: set when a member referred this person through the quest's
  // Refer form (worker_refer_candidate). Null for every other row. Its note is
  // the referrer's own words about a third party: admin-only, never translated.
  referral: CandidateReferral | null;
  // 20260922140000: set when the headhunter found that this member COMPLETED a
  // $0 task for the same creator, or one sharing a skill with this $0 quest.
  // Null for everyone else. It is evidence, never a contact permission.
  volunteerHistory: CandidateVolunteerHistory | null;
}

/** Who referred a candidate, and what their link and note carry (20260923100000). */
export interface CandidateReferral {
  referredByUserId: string;
  /** From public_profiles; null when the referrer has no display name. */
  referredByName: string | null;
  /** The referrer's link code — `?ref=r_<code>` credits them when this person applies. */
  code: string | null;
  note: string | null;
}

/** One other quest a candidate identity is also matched to (DEV-450). */
export interface OtherQuestMatch {
  questId: string;
  questTitle: string | null;
  status: AdminCandidateStatus;
}

/**
 * Whether a candidate is already inside weXare — either an internal member or an
 * external person who joined via outreach. Such people need no manual outreach and
 * are shown as a "weXare user" rather than a prospect. Single source of truth for
 * this rule so the badge and the drawer's read-only gate can't drift apart.
 */
export function isConvertedMember(
  candidate: Pick<AdminQuestCandidate, "source" | "convertedUserId">,
): boolean {
  return candidate.source === "internal" || candidate.convertedUserId != null;
}

/**
 * DEV-822 — whether a lead holds this candidate's person, and if so whether they
 * have claimed it.
 *
 * THREE STATES, NOT A BOOLEAN, because "claimed" is not a stronger "unclaimed" —
 * it is a different situation with a different correct affordance:
 *
 *   none      — no lead holds them. "Create lead" is a real offer.
 *   unclaimed — a lead exists and is still a live prefill an admin may correct
 *               (DEV-817). Creating another is a no-op: the RPC is idempotent on
 *               the contact and would hand back this same lead.
 *   claimed   — the person has logged in and their profile supersedes the
 *               prefill. There is nothing left to prefill, and
 *               admin_create_lead_from_candidate RAISES on this case rather than
 *               returning quietly.
 *
 * Collapsing the last two would put a button in front of an admin whose only
 * possible outcome is that error toast.
 */
export type CandidateLeadState = "none" | "unclaimed" | "claimed";

/**
 * Single source of truth for the rule above, mirroring `isConvertedMember`
 * deliberately — same file, same `Pick<>` shape, same reason. The row's badge,
 * the drawer button's label and that button's `disabled` all branch on this, and
 * a second reading of "does this candidate have a lead?" is precisely how the
 * badge and the button come to disagree.
 *
 * DERIVED, NEVER STORED. `leadClaimedAt` is the fact; the state is computed. A
 * `lead_status` column from the RPC would be a third encoding of
 * `leads.claimed_at`, which already has `CONSTRAINT leads_claim_pair` keeping it
 * in step with `claimed_by`.
 *
 * A CLAIM TIMESTAMP WITHOUT A LEAD READS AS `none`. The database cannot produce
 * that pair, but a mapper bug could, and treating it as `claimed` would hide the
 * create button on evidence of a lead that is not there. `leadId` is the
 * existence question; `leadClaimedAt` only refines an answer already given.
 */
export function candidateLeadState(
  candidate: Pick<AdminQuestCandidate, "leadId" | "leadClaimedAt">,
): CandidateLeadState {
  if (candidate.leadId == null) return "none";
  return candidate.leadClaimedAt != null ? "claimed" : "unclaimed";
}

/**
 * Server-side sort keys for the candidate list. Mirrors the columns the
 * admin_list_quest_candidates RPC knows how to ORDER BY — extending this union
 * requires teaching the RPC the new key. Sorting lives server-side because the
 * table is paginated: reordering only the current page would be misleading.
 */
export const CANDIDATE_SORT_KEYS = ["score", "created", "name"] as const;
export type CandidateSortKey = (typeof CANDIDATE_SORT_KEYS)[number];
export type SortDir = "asc" | "desc";

export interface AdminCandidateFilters {
  /** Scope to a blockchain network (Amoy 80002 / Polygon 137) via quests.chain_id. */
  chainId?: number;
  /** Filter candidates to a specific quest. */
  questId?: string;
  source?: CandidateSource | "all";
  status?: AdminCandidateStatus | "all";
  search?: string;
  hasContact?: boolean;
  /** true = only not-yet-joined people (the external prospects-to-invite worklist). */
  unconverted?: boolean;
  // ─── Match-tier filter (W-C) ────────────────────────────
  // A UI tier chip is translated to this score range via candidateTiers'
  // `tierScoreFilter` so the cut points live in exactly one place. The RPC applies
  // the range generically (no hardcoded thresholds server-side).
  //
  // Contract: `unscored` and the numeric band (`minScore`/`maxScore`) are mutually
  // exclusive — a tier is EITHER the unscored bucket OR a score range, never both.
  // `tierScoreFilter` guarantees this. Both adapters and the RPC AND all three
  // clauses, so sending `unscored` together with a band intersects to the empty set
  // in every layer (consistent, but not a meaningful query).
  /** Inclusive lower score bound (`score >= minScore`). */
  minScore?: number;
  /** Exclusive upper score bound (`score < maxScore`). */
  maxScore?: number;
  /** Select only unscored candidates (null / negative score) — the "unscored" tier. */
  unscored?: boolean;
  // ─── Facet filters (W-C) ────────────────────────────────
  /** Case-insensitive substring match against location city OR country. */
  location?: string;
  /** Case-insensitive substring match against any of the candidate's skills. */
  skill?: string;
  // ─── Sort (W-C) ─────────────────────────────────────────
  /** Sort column; defaults to score (desc) server-side when omitted. */
  sort?: CandidateSortKey;
  /** Sort direction; defaults to desc server-side when omitted. */
  sortDir?: SortDir;
  limit?: number;
  offset?: number;
}

/** A published/negotiating quest with candidate counts — pending assignment panel. */
export interface PendingQuest {
  questId: string;
  title: string;
  status: QuestStatus;
  createdAt: string;
  internalCount: number;
  externalCount: number;
  invitedCount: number;
  pendingCount: number;
  dismissedCount: number;
}

export interface PendingQuestFilters {
  /** Scope to a blockchain network (Amoy 80002 / Polygon 137) via quests.chain_id. */
  chainId?: number;
  search?: string;
  limit?: number;
  offset?: number;
}

export const HEADHUNTER_RUN_STATUSES = [
  "ok",
  "partial",
  "error",
  "skipped",
] as const;
export type HeadhunterRunStatus = (typeof HEADHUNTER_RUN_STATUSES)[number];

/** DEV-507: which surface produced a run — a real quest, or the raw-search testbed. */
export const HEADHUNTER_RUN_KINDS = ["quest", "testbed"] as const;
export type HeadhunterRunKind = (typeof HEADHUNTER_RUN_KINDS)[number];

/** DEV-551: the quest/profile classification the orchestrator resolved. */
export type WorkContext = "local_business" | "local_individual" | "employed_professional" | "independent_creator";

/**
 * DEV-497: how a cost number was derived. `none` = the run made no paid calls,
 * so its $0 is exact rather than an approximation.
 */
export type CostBasis = "settled" | "estimated" | "mixed" | "none";

/**
 * DEV-497: the marker a spend figure carries, given how it was derived.
 *   ""  — exact: settled on-chain, or a genuine zero.
 *   "~" — approximate: the ENDPOINT_COSTS estimate, in whole or in part.
 *   "?" — unknown: recorded before DEV-497 shipped (basis null/undefined).
 *
 * `?` rather than `~` for the unknown case on purpose: a pre-DEV-497 row was
 * ALWAYS an estimate, but rendering it bare would present it as exact — the
 * precise claim this ticket exists to stop making. PURE — safe to unit test.
 */
export function spendBasisPrefix(basis: CostBasis | null | undefined): "" | "~" | "?" {
  if (basis == null) return "?";
  if (basis === "settled" || basis === "none") return "";
  return "~";
}

/** One Headhunter worker invocation — the activity log for the DO node. */
export interface HeadhunterRun {
  id: string;
  chainId: number;
  questId: string | null;
  questTitle: string | null;
  status: HeadhunterRunStatus;
  kind: HeadhunterRunKind;
  skippedReason: string | null;
  internalFound: number;
  externalFound: number;
  /** DEV-507: how many found candidates carried an email or phone. */
  contactableFound: number;
  candidatesPersisted: number;
  spendUsdc: number;
  walletBalanceAfter: number | null;
  durationMs: number | null;
  errorMessage: string | null;
  startedAt: string;
  finishedAt: string | null;
  /** DEV-551: the quest/profile classification at run time (null when the run crashed before plan resolution). */
  workContext: WorkContext | null;
  /** DEV-551: short human-readable summary of what was searched (titles/skills/location). */
  queryLabel: string | null;
  /** DEV-497: how the spend number was derived — "settled", "estimated", "mixed", or null (pre-migration). */
  spendBasis: CostBasis | null;
}

export interface HeadhunterRunFilters {
  status?: HeadhunterRunStatus | "all";
  kind?: HeadhunterRunKind | "all";
  chainId?: number;
  limit?: number;
  offset?: number;
}

export type HeadhunterHealthStatus = "up" | "degraded" | "down" | "unknown";

/** The worker's live heartbeat (single row), surfaced as the health panel. */
export interface HeadhunterHealth {
  status: HeadhunterHealthStatus;
  chainId: number;
  walletBalanceUsdc: number | null;
  dailySpendUsdc: number | null;
  dailyCapUsdc: number | null;
  version: string | null;
  lastError: string | null;
  lastSeenAt: string | null;
  updatedAt: string;
}

export type LogEventLevel = "info" | "warn" | "error";

export const LOG_EVENT_LEVELS = ["info", "warn", "error"] as const;

/**
 * One step-level structured log emitted by the headhunter worker (funnel step,
 * geofence drop, enrich failure, …). `runId` is null for events outside a run
 * (worker start, sweeps). `fields` is sanitized worker-side — no PII.
 */
export interface HeadhunterLogEvent {
  id: string;
  chainId: number;
  runId: string | null;
  /**
   * DEV-535: which surface emitted this event, denormalised from the run at
   * write time (see the log_events kind migration for why it is not a join).
   */
  kind: HeadhunterRunKind;
  event: string;
  level: LogEventLevel;
  message: string;
  fields: Record<string, unknown>;
  createdAt: string;
}

export interface HeadhunterLogFilters {
  /** When set, fetch one run's events (chronological); otherwise the live tail. */
  runId?: string;
  chainId?: number;
  level?: LogEventLevel | "all";
  /**
   * DEV-535: scope the live tail to one run kind. Reads the `kind` column the
   * worker stamps on each event (denormalised from the run) — NOT a join to
   * headhunter_runs, whose summary row does not exist while a run is in flight.
   * "all" / omitted applies no predicate, matching the run-metrics RPC.
   *
   * Ignored in practice by the drill-down (runId set): one run's events are all
   * one kind, and run_id already scopes them.
   */
  kind?: HeadhunterRunKind | "all";
  limit?: number;
  offset?: number;
}

/** Aggregated run stats for the headhunter info panel. */
export interface HeadhunterRunStats {
  totalRuns: number;
  totalSpendUsdc: number;
  totalInternalFound: number;
  totalExternalFound: number;
  /** DEV-520: how many found candidates carried an email or phone. */
  totalContactableFound: number;
  totalCandidatesPersisted: number;
  firstRunAt: string | null;
  lastRunAt: string | null;
}

// ─── Headhunter analytics (DEV-363) ─────────────────────
// Filterable admin analytics over the sourcing funnel, backed by three
// SECURITY DEFINER + is_admin() RPCs (admin_headhunter_run_metrics /
// _candidate_funnel / _drop_breakdown). Property names below mirror the RPC
// JSONB keys verbatim — the RPCs already return camelCase, so no mapper.

export interface HeadhunterMetricsFilters {
  chainId: number;
  from?: string; // ISO — undefined means "no lower bound" (RPC p_from IS NULL)
  to?: string; // ISO — undefined means "no upper bound"
  questId?: string;
  // "all" (and undefined) map to a null p_source at the adapter; the funnel RPC
  // only accepts 'internal' | 'external' | null and RAISEs otherwise.
  source?: CandidateSource | "all";
  // Client-side only: no analytics RPC accepts a status param. PR-5 applies
  // this in the UI; the adapter ignores it.
  status?: AdminCandidateStatus | "all";
  // DEV-520: 'quest' (default) | 'testbed' | 'all'. Maps to the RPC's p_kind.
  // Testbed runs corrupt thinQuestRuns and costPerPersisted, so this axis is
  // not cosmetic — see the migration header.
  kind?: HeadhunterRunKind | "all";
}

export interface HeadhunterRunMetrics {
  totalRuns: number;
  okRuns: number;
  partialRuns: number;
  errorRuns: number;
  skippedRuns: number;
  successRate: number;
  totalSpendUsdc: number;
  avgSpendUsdc: number;
  avgDurationMs: number;
  /**
   * Runs whose INTERNAL member pool produced fewer than 3 matches.
   *
   * DEV-737: internal coverage only. This used to double as "runs that fell
   * through to the paid external funnel" — true while the external leg was gated
   * behind the worker's THIN_THRESHOLD, and false since that gate was deleted:
   * every run now reaches the external leg. For paid-vendor usage read
   * totalSpendUsdc / totalExternalFound instead.
   */
  thinQuestRuns: number;
  totalInternalFound: number;
  totalExternalFound: number;
  totalCandidatesPersisted: number;
  costPerPersisted: number;
  firstRunAt: string | null;
  lastRunAt: string | null;
}

// bucket is a STRING ("1".."12") — from width_bucket(score, 0, 1.2, 12).
export interface ScoreBucket {
  bucket: string;
  internal: number;
  external: number;
}

export interface HeadhunterCandidateFunnel {
  suggested: number;
  reviewed: number;
  invited: number;
  dismissed: number;
  convertedCount: number;
  conversionRate: number;
  dismissRate: number;
  outreachSentCount: number;
  scoreHistogram: ScoreBucket[];
  scoreMedianExternal: number | null;
}

export interface DropStage {
  key: string;
  before: number;
  after: number;
  dropped: number;
}

export interface HeadhunterDropBreakdown {
  stages: DropStage[];
  enrichFailures: number;
}

// ─── Outreach review (DEV-351 R-B) ──────────────────────
// The human approve/edit gate for Wexare-mediated outreach. The worker drafts a
// recruiting email per sourced candidate as 'pending_review'; an admin edits the
// subject/body and approves or rejects; only approved drafts are ever dispatched.

export type OutreachDraftStatus =
  | "pending_review"
  | "approved"
  | "rejected"
  | "sent"
  | "failed";

/** external = cold outreach; internal = recruiting email to a consenting member. */
export type OutreachDraftKind = "external" | "internal";

export interface OutreachDraftFilters {
  /** Scope to a blockchain network (Amoy 80002 / Polygon 137) via quests.chain_id. */
  chainId?: number;
  status?: OutreachDraftStatus;
  limit?: number;
  offset?: number;
}

/** DEV-788: which identifier a draft is addressed to. */
export type OutreachChannel = "email" | "sms" | "whatsapp";

export interface OutreachDraft {
  id: string;
  candidateId: string;
  questId: string;
  questTitle: string | null;
  /**
   * DEV-788: null for a phone-only candidate. Was non-null for every draft
   * before the phone channel existed, which is why `channel` — not the
   * presence of this field — is what decides how a draft is addressed.
   */
  email: string | null;
  fullName: string | null;
  locationCountry: string | null;
  subject: string;
  body: string;
  status: OutreachDraftStatus;
  kind: OutreachDraftKind;
  failureReason: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  sentAt: string | null;
  createdAt: string;
  updatedAt: string;
  // Candidate enrichment (DEV-379) — joined from quest_candidates at list time.
  // Null when the candidate row is missing (orphaned draft) or fields unset.
  candidateScore: number | null;
  candidateSkills: string[] | null;
  candidateLinkedinUrl: string | null;
  candidateSourceUrl: string | null;
  candidateEmploymentTitle: string | null;
  candidateLocationCity: string | null;
  // Dismissal (DEV-380) — set when status === "rejected".
  candidateDismissalReason: DismissalReason | null;
  candidateDismissalNote: string | null;
  // AI profile (DEV-400) — null until generated in the Staffing view.
  candidateAiProfile: string | null;
  // DEV-503: true when `candidateSkills` is an ECHO of the search query that
  // found this person, not anything a source attested. This is the APPROVAL
  // surface, so the queue must not present our own terms as a reason to contact
  // someone — and must not count them as a skill match.
  candidateSkillsFromQuery: boolean;
  // DEV-376. `intro` is the structured human input; `bodyHtml` is the text/html
  // part (null on drafts that predate the card email); `bodyOverride` is TRUE
  // once an admin hand-edited, and then nothing may regenerate the body.
  intro: string | null;
  bodyHtml: string | null;
  bodyOverride: boolean;
  // DEV-788. Dispatch still sends email only; an sms/whatsapp draft is an
  // admin-visible record for the by-hand path until the SMS outreach spec lands.
  channel: OutreachChannel;
  phone: string | null;
}

export type DraftOutreachSkipReason =
  | "already_drafted"
  | "suppressed"
  | "cooldown"
  | "geofenced";

/** The server's own idempotency guard, as opposed to an opt-out/cooldown/geofence
 * skip — callers branch on this specific reason to render a dedicated UI state
 * rather than a generic "couldn't draft" message. */
export const ALREADY_DRAFTED_SKIP_REASON: DraftOutreachSkipReason = "already_drafted";

/** Result of an on-demand draft request. `not_found`/errors surface as thrown errors. */
export type DraftOutreachResult =
  | { status: "created"; draftId: string }
  | { status: "skipped"; reason: DraftOutreachSkipReason };

/**
 * Result of a MANUAL testbed outreach send.
 *
 * Deliberately WITHOUT the worker's `not_found` arm: the worker route maps
 * not_found to HTTP 404, which the adapter surfaces as a thrown error, so it can
 * never arrive here as a resolved value. Keeping it out stops a dead branch from
 * being written in the UI.
 */
export type TestbedOutreachResult =
  | { status: "sent" }
  | { status: "copied" }
  | { status: "skipped"; reason: "suppressed" | "cooldown" | "disabled" | "no_email" }
  | { status: "failed"; reason: string };

/**
 * Result of an admin's manual "send now" on ONE approved outreach draft (DEV-795).
 *
 * Like TestbedOutreachResult, deliberately WITHOUT the worker's `not_found` arm:
 * the route maps it to HTTP 404, which the adapter surfaces as a thrown error.
 *
 * `not_approved` IS kept, because it resolves normally: it means the draft's
 * status changed under the admin (another tab approved, rejected or already sent
 * it) between the list loading and the click, and the UI should say so rather
 * than claim a send.
 */
/**
 * WHICH outreach setting the worker found missing, mirroring `OutreachGap` in
 * services/headhunter-worker/src/outreach/dispatchOne.ts. The names are the env
 * vars an operator sets, and `draft_links` is not a setting at all but damage
 * already stored on the row — see that file's note. Kept as a widened `string`
 * fallback because the worker deploys independently of the app: an older worker
 * sends no list, and a newer one may name a gap this build has no phrase for.
 */
export type OutreachGap =
  | "OUTREACH_FROM_EMAIL"
  | "OUTREACH_APP_BASE_URL"
  | "OUTREACH_HMAC_SECRET"
  | "OUTREACH_PHYSICAL_ADDRESS"
  | "draft_links";

export type SendOutreachDraftResult =
  | { status: "sent" }
  | { status: "skipped"; reason: "suppressed" | "cooldown" | "disabled" }
  // `missing` is optional HERE but required in the worker: this arm also models
  // the response of a worker deployed before the field existed.
  | { status: "skipped"; reason: "misconfigured"; missing?: (OutreachGap | string)[] }
  | { status: "failed"; reason: string }
  | { status: "not_approved"; current: string };

/** Result of an on-demand contact re-enrichment request. `not_found`/errors surface as thrown errors. */
export type ReenrichCandidateResult =
  | { status: "found"; email: string }
  | { status: "no_email_found" };

export interface CandidateUpdateInput {
  fullName?: string | null;
  email?: string | null;
  phone?: string | null;
  linkedinUrl?: string | null;
  employmentTitle?: string | null;
  locationCity?: string | null;
  skills?: string[];
}

/**
 * DEV-TBD: input for adding a quest candidate by hand.
 *
 * Mirrors the admin_add_quest_candidate RPC parameters. `questId` is the target
 * quest; all other fields are the candidate's details. At least one of
 * `email`, `phone`, or `linkedinUrl` is required — a contactless row is dead
 * data by construction.
 */
export interface QuestCandidateCreateInput {
  questId: string;
  fullName: string;
  email?: string | null;
  phone?: string | null;
  linkedinUrl?: string | null;
  locationCity?: string | null;
  locationCountry?: string | null;
  employmentTitle?: string | null;
  skills?: string[];
  notes?: string | null;
}

/**
 * Loose "looks like an email" check shared by the manual-email-entry UI and its
 * mock-service fixture. Mirrors (but cannot literally share code with) the same
 * pattern enforced server-side by admin_set_candidate_email() in
 * supabase/migrations/20260721170000_admin_set_candidate_email.sql — keep the
 * two in sync if either changes.
 */
export const CANDIDATE_EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * Loose "looks like a phone number" check, the sibling of
 * CANDIDATE_EMAIL_PATTERN above and shared by every candidate edit surface.
 *
 * Deliberately permissive: the product sources in several countries and the
 * vendors return national, international and punctuated forms. It only rejects
 * input that could not be a number at all, so an admin correcting a row is not
 * fighting a strict parser. `normalizePhone` in the worker
 * (services/headhunter-worker/src/funnel/contactExtract.ts) is the real
 * normaliser, and is not importable from the browser bundle — hence this copy,
 * exactly as isValidEmailish mirrors a server-side regex it cannot share.
 *
 * At least one digit is required, which is what makes "+" and prose fail.
 */
export function isValidPhoneish(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (!/\d/.test(trimmed)) return false;
  return /^\+?[\d\s\-().]+$/.test(trimmed);
}

/**
 * Result of a manual headhunter run (admin-run-headhunter → worker force run).
 *
 * The edge function is fire-and-forget: it kicks off the worker call via
 * `EdgeRuntime.waitUntil` and returns 202 immediately, without waiting for or
 * reading the worker's response body (this avoids a 504 when the funnel run
 * takes several minutes). As a result the client only ever sees `{ok: true,
 * queued: true}` on the happy path — `skipped` is never populated through
 * this path today, and worker-side outcomes (skip reasons, run failures) are
 * only observable later via `useHeadhunterRuns` polling, not from this call's
 * return value. `count` is likewise unused by the current edge fn.
 *
 * Errors thrown from this call are edge-function-level failures only (auth
 * rejected, admin check failed, missing questId, misconfigured worker URL) —
 * not worker/funnel-level errors, which land in the run log instead.
 *
 * The `skipped`/`count` fields are kept for a future synchronous or
 * webhook-based contract; don't rely on them being populated today.
 */
export interface RunHeadhunterResult {
  ok: boolean;
  count?: number;
  skipped?: string;
  /** True when the edge function fired the worker asynchronously (202 Accepted).
   *  The run result is not yet available — poll useHeadhunterRuns for completion. */
  queued?: boolean;
}

/**
 * Admin diagnostic-only: one raw candidate returned by a StableEnrich
 * people-search invoked via the admin-search-test edge function, bypassing
 * the quest funnel, persistence, and scoring entirely (see mapPerson /
 * externalPeopleSearch in headhunter-worker). `score` is always 0 here.
 */
export interface SearchTestbedCandidate {
  id?: string;
  source: string;
  memberId?: string | null;
  fullName?: string;
  locationCity?: string | null;
  locationCountry?: string | null;
  skills: string[];
  employmentTitle?: string | null;
  linkedinUrl?: string | null;
  email?: string | null;
  phone?: string | null;
  /**
   * DEV-807: an internal member's own connected email channel, present ONLY on
   * rows mapped from a quest candidate (questCandidateCardShape). The search
   * testbed never sets it — the worker's `/search` payload has no such field,
   * because a testbed run resolves no member's channels.
   *
   * Optional for the same reason `sourceLeg` is: absent is the honest answer for
   * every producer that has no concept of it, and a required field would force
   * one to be invented. Absent and null read identically — "no member channel" —
   * which is also the correct reading for every external row.
   */
  memberEmail?: string | null;
  emailVerified?: boolean | null;
  /** DEV-473: the single platform this candidate was sourced from (e.g.
   * "facebook", "github"), or absent/null for FullEnrich-sourced hits. */
  sourcePlatform?: string | null;
  /** DEV-481: per-platform public profile URLs captured by search
   * (e.g. {"tiktok": "https://tiktok.com/@x"}), or absent/null. */
  socialProfiles?: Record<string, string> | null;
  /**
   * DEV-505: which funnel leg produced this candidate — run-level attribution
   * (the `web_fallback_used` warning) could not say which SOURCE a given card
   * came from. Absent on workers predating DEV-505; render as unknown, never
   * crash.
   *
   * DEV-552: `firecrawl` added. Mirrors the worker's copy in
   * services/headhunter-worker/src/internalSearch.ts.
   */
  sourceLeg?: "internal" | "people_search" | "social" | "maps" | "web_fallback" | "firecrawl" | null;
  /**
   * DEV-505: the match score, or NULL when scoring never ran.
   *
   * Nullable because the testbed never invokes the orchestrator, so its
   * candidates are genuinely unscored — the worker used to send a `0` sentinel
   * that this panel rendered as a real "Score 0", making every web-fallback hit
   * look like a computed-worst match. Also absent on pre-DEV-505 workers.
   */
  score?: number | null;
  /**
   * DEV-474: "person" | "business". A Maps hit is a BUSINESS listing, not a
   * person with a missing employer — rendering the two identically is what made
   * a trade search look broken. Absent on workers predating DEV-474; treat
   * absent as "person".
   */
  entityType?: "person" | "business" | null;
  /** DEV-474: Google Places id — the identity basis for a business listing. */
  mapsPlaceId?: string | null;
  /**
   * DEV-499b: Google Maps formatted street address for a business listing.
   * Absent on workers predating DEV-499b; render as absent, never crash.
   */
  formattedAddress?: string | null;
  /**
   * DEV-499b: clickable Google Maps URI for a business listing. Absent when the
   * worker could not build one (no vendor URI and no place id).
   */
  mapsUri?: string | null;
  /**
   * DEV-568: DEV-503's query-provenance flags, which this interface was missing.
   *
   * `true` means the value is an ECHO of the operator's own search input, not
   * something the source attested about this candidate: `skills` copied from the
   * filter chips, `locationCity`/`locationCountry` backfilled from the query's
   * location. The worker has set both since DEV-491/DEV-503 and
   * `POST /admin/search-test` returns its `Candidate[]` verbatim, so both have
   * always been on the wire — they were declared on `AdminQuestCandidate` (the
   * real-quest type, whose card and drawer honour them) and nowhere here, so the
   * testbed rendered our own query back as candidate evidence.
   *
   * Optional to match this interface's convention for fields a worker might not
   * send. Absent is treated as "not echoed" (unmarked), which is safe rather than
   * merely convenient: the flags predate `headhunter_testbed_candidates`
   * (migration 20260802130000), so no stored payload can lack them, and a live
   * run's worker always sets them.
   */
  skillsFromQuery?: boolean;
  locationFromQuery?: boolean;
}

/**
 * DEV-562: one stored candidate from a past testbed run, read back from
 * `headhunter_testbed_candidates` for the run-log drill-down.
 *
 * `candidate` is the payload the worker stored VERBATIM, so it is exactly the
 * shape the live Raw Search panel renders — the same cards and drawer serve
 * both. It is typed as SearchTestbedCandidate on the optimistic assumption that
 * the worker that wrote it agreed with today's shape; nearly every field on
 * that interface is optional-and-nullable (workers predating DEV-473/481/499b
 * omitted theirs), so an older payload renders as absent fields rather than
 * crashing. The exceptions are `source` and `skills`, which are declared
 * required: the renderers guard `skills` at runtime because the type cannot
 * police a row written by a different build. Do not add a required field to
 * SearchTestbedCandidate without checking this read path.
 */
export interface StoredTestbedCandidate {
  id: string;
  runId: string;
  /** Position in the array the worker returned — funnel order. */
  ordinal: number;
  candidate: SearchTestbedCandidate;
}

export interface TestbedCandidateFilters {
  runId: string;
  limit?: number;
  offset?: number;
}

export interface SearchTestbedParams {
  titles: string[];
  skills: string[];
  location?: string;
  limit?: number;
  /** DEV-473: platform selection (facebook/instagram/tiktok/reddit/twitter/github/bluesky).
   * Always sent as an array, even empty — omitted/empty means "search none of these". */
  platforms?: string[];
  /**
   * ISO alpha-2 from the Entender step, carried straight through to the worker.
   *
   * Not cosmetic and not derivable downstream: it is the key into the
   * country-aware trade-marketplace table that decides which sites the Exa leg
   * is restricted to, and the worker can only recognise twenty full country
   * NAMES — so "Seattle, USA" alone yields no country and an unrestricted search.
   */
  countryCode?: string;
  /**
   * DEV-691: the operator's person/business choice for this search, carried
   * straight through to the worker alongside `countryCode` above. The testbed
   * panel always sends this, including "either" — that is a real answer
   * meaning "I don't mind", not an absence, and sending it explicitly stops
   * the worker from substituting its own work-context projection for what the
   * operator actually said.
   */
  targetType?: string;
}

/**
 * DEV-494: real vendor spend for one raw-search testbed run, reported by the
 * worker's /admin/search-test route.
 *
 * NB: `spentTodayUsd` comes from an in-memory SpendTracker in the worker
 * process — it is reset by any worker restart or redeploy, and two worker
 * instances would each track their own. Treat it as a FLOOR, not an audited
 * total. The durable figure is `walletBalanceUsd`.
 */
export interface SearchTestbedSpend {
  /** Cost of this run alone, across every paid call it made. */
  runCostUsd: number;
  spentTodayUsd: number;
  dailyCapUsd: number;
  /** Null when the balance read failed — never a reason to fail the search. */
  walletBalanceUsd: number | null;
  /** DEV-497: how the cost number was derived — "settled", "estimated", or "mixed". */
  costBasis?: CostBasis;
}

export interface SearchTestbedResult {
  candidates: SearchTestbedCandidate[];
  warnings?: string[];
  /** Absent on worker builds predating DEV-494 — render as "—", never crash. */
  spend?: SearchTestbedSpend;
}

/**
 * DEV-494: a failed testbed search that may still have cost money. A run can
 * throw *after* the wallet was charged (the people-search succeeds, a social
 * platform call then fails), which is precisely when the admin needs the figure —
 * so the spend is carried on the error rather than discarded with the response
 * body.
 */
export class SearchTestbedError extends Error {
  readonly spend?: SearchTestbedSpend;

  constructor(message: string, spend?: SearchTestbedSpend) {
    super(message);
    this.name = "SearchTestbedError";
    this.spend = spend;
  }
}

export interface ParsedSearchQuery {
  titles: string[];
  skills: string[];
  location: string;
  /**
   * DEV-563: the worker's prediction of where a search with these filters would
   * go, computed by the same `resolveWorkContext` + `selectSourceStrategy` the
   * real run calls. All three fields are OPTIONAL and absent together — the
   * edge function's worker call is best-effort, so a worker that is down or
   * unset degrades Entender to filling the text fields only.
   *
   * `undefined` and `[]` mean different things and the UI must keep them apart:
   * absent is "no prediction, leave the operator's checkboxes alone", empty is
   * "predicted nothing would run, clear them".
   */
  workContext?: string;
  /** Ordered source legs — see `sourceStepLabel` in `@/lib/sourcePlan`. */
  plan?: string[];
  /** Platform checkbox keys the plan's social legs imply, in plan order. */
  platforms?: string[];
  /**
   * ISO alpha-2 the parse step inferred for the location, or "" when it could
   * not (no location, or an ambiguous city). Always present, unlike the three
   * prediction fields above — it comes from the parse itself, not from the
   * best-effort worker call.
   */
  countryCode?: string;
  /**
   * DEV-691: whether the parse read this as a search for an individual, a
   * company, or either. The parse always sets this — to "" when the model
   * gave no usable answer, in which case the worker projects one from the
   * work context. It stays optional in the type, not "always present" like
   * the parse output otherwise, because a worker or edge function predating
   * this field simply omits the key rather than sending "".
   */
  targetType?: string;
}
